import { useContext, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { AuthContext } from '@/context/auth-context'
import signOutOfApp from '@/firebase/auth/sign-out'
import { PostSignupPhoneStatus } from '@/firebase/auth/auth-jwt-schema'
import { PostSignupPhoneStep } from '@/onboarding/components/PostSignupPhoneStep'
import { postCompletePostSignupPhone, postStartPostSignupPhone } from '@/onboarding/fetch/post-signup-phone'
import { isPostSignupPhoneEligible, isPostSignupPhoneEnrollment } from '@/onboarding/post-signup-phone-routing'
import { ApiError } from '@/util/errors/api-error'

type StartState = 'idle' | 'loading' | 'error'

export default function PostSignupPhonePage() {
  const { loading, refreshUserAccountData, userAccountData } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [startState, setStartState] = useState<StartState>('idle')
  const [startAttempt, setStartAttempt] = useState(0)
  const startRequestRunningRef = useRef(false)
  const phoneCollectionEnabled = kGetFeatureFlagStatus(kFeatureFlags.POST_SIGNUP_PHONE_COLLECTION)
  const enrollmentRequested = isPostSignupPhoneEnrollment(location.state)
  const currentStatus = userAccountData?.postSignupPhoneStatus ?? null
  const eligible = Boolean(userAccountData && isPostSignupPhoneEligible(userAccountData))
  const shouldStart = phoneCollectionEnabled && eligible && enrollmentRequested && currentStatus === null

  function handleSignOut() {
    void signOutOfApp(navigate)
  }

  useEffect(() => {
    if (!shouldStart || startRequestRunningRef.current) return

    startRequestRunningRef.current = true
    setStartState('loading')

    void postStartPostSignupPhone()
      .then(async () => {
        const refreshedUserAccountData = await refreshUserAccountData()
        const refreshedStatus = refreshedUserAccountData ? refreshedUserAccountData.postSignupPhoneStatus : null
        if (refreshedStatus === null) throw new Error('Post-signup phone status was not refreshed')
      })
      .catch(() => {
        setStartState('error')
      })
      .finally(() => {
        startRequestRunningRef.current = false
      })
  }, [refreshUserAccountData, shouldStart, startAttempt])

  if (loading) return <FullPageLoader />
  if (!phoneCollectionEnabled || !eligible) return <Navigate to="/dashboard" replace />
  if (currentStatus === PostSignupPhoneStatus.COMPLETED) return <Navigate to="/dashboard" replace />

  if (currentStatus === PostSignupPhoneStatus.REQUIRED) {
    return (
      <PostSignupPhoneStep
        state="ready"
        onSignOut={handleSignOut}
        onComplete={async ({ phone, textMessageConsent }) => {
          try {
            await postCompletePostSignupPhone({ phone, text_message_consent: textMessageConsent })
          } catch (error) {
            if (error instanceof ApiError && error.status === 422) return 'invalid-phone'
            throw error
          }

          const refreshedUserAccountData = await refreshUserAccountData()
          if (!refreshedUserAccountData || refreshedUserAccountData.postSignupPhoneStatus !== PostSignupPhoneStatus.COMPLETED) {
            throw new Error('Post-signup phone completion was not refreshed')
          }
          return 'completed'
        }}
      />
    )
  }

  if (!enrollmentRequested) return <Navigate to="/dashboard" replace />

  if (startState === 'error') {
    return (
      <PostSignupPhoneStep
        state="start-error"
        onSignOut={handleSignOut}
        onRetry={() => {
          setStartState('idle')
          setStartAttempt((attempt) => attempt + 1)
        }}
      />
    )
  }

  return <FullPageLoader />
}
